import numeral from "numeral";
import moment from "moment";
import { parseMultipleAuthors } from "./utils";

export const CITATION = {
  mla: "mla",
  apa: "apa",
  har: "har",
  van: "van",
  abnt: "abnt"
};

export const getCitationInfo = (state, isbn) => {
  let info = state.citations[isbn];
  return info && info.info ? info.info : null;
};

export const isFetching = (state, isbn) => {
  const info = state.citations[isbn];
  return info ? info.isFetching : false;
};

const getInitial = (names, dots = true) => {
  if (names && names.trim()) {
    return names.split(" ").reduce((initials, name) => {
      return name ? initials + name[0].toUpperCase() + (dots ? "." : "") : "";
    }, "");
  }
  return "";
};

export const getCitation = (state, isbn, format) => {
  const citationInfo = getCitationInfo(state, isbn);
  if (citationInfo) {
    return getCitationText(citationInfo, format);
  }
};

export const getCitationText = (citationInfo, format) => {
  switch (format) {
    case CITATION.apa:
      return getApaCitationForBook(citationInfo);
    case CITATION.mla:
      return getMlaCitationForBook(citationInfo);
    case CITATION.har:
      return getHarvardCitationForBook(citationInfo);
    case CITATION.abnt:
      return getABNTCitationForBook(citationInfo);
    case CITATION.van:
      return getVancouverCitationForBook(citationInfo);
  }
};

export const getMlaCitationForBook = citationInfo => {
  let citation = parseMultipleAuthors(citationInfo, CITATION.mla);
  if (!citation && citationInfo.author.length > 0) {
    let author = citationInfo.author[0];
    if (author.last && author.last.trim().length > 0) {
      citation += author.last;
    }
    if (author.first && author.first.trim().length > 0) {
      citation += ", " + author.first;
      if (author.middle && author.middle.trim().length > 0) {
        citation += " " + getInitial(author.middle);
      }
    } else {
      if (!citation) {
        citation = "";
      }
    }
    for (let i = 1; i < citationInfo.author.length; i++) {
      author = citationInfo.author[i];
      citation += ", " + author.first + " " + author.last;
    }
    if (citation.length > 1 && citation[citation.length - 1] !== ".") {
      citation += ".";
    }
  }
  if (citationInfo.title) {
    citation += " <em>" + citationInfo.title;
  }
  if (
    citationInfo.edition &&
    citationInfo.edition.length > 0 &&
    parseInt(citationInfo.edition, 10) > 1
  ) {
    citation +=
      ", " +
      i18n.t("library.book.edition", {
        edition: numeral(parseInt(citationInfo.edition, 10)).format("0o")
      }) +
      "</em>.";
  } else {
    citation += "</em>.";
  }

  if (citationInfo.publisher && citationInfo.publisher.length > 0) {
    citation += " " + citationInfo.publisher;
  }
  if (citationInfo.publicationDate && citationInfo.publicationDate.length > 0) {
    // MLA date format only returns the publication date year
    citation += ", " + moment.utc(citationInfo.publicationDate).format("YYYY");
  }
  citation += ".";
  citation += ` [${Fraction.Brand.brand}].`;

  return citation;
};

export const getHarvardCitationForBook = citationInfo => {
  let citation = parseMultipleAuthors(citationInfo, CITATION.har);
  if (!citation && citationInfo.author.length) {
    citation += citationInfo.author.reduce((list, author, index, authors) => {
      let { first = "", last = "" } = author;
      if (index > 0 && authors.length === index + 1) {
        list += ` ${i18n.t("base.and")} `;
      }
      list += last && last.trim() ? last : "";
      list += first && first.trim() ? ", " + getInitial(first) : "";
      return list;
    }, "");
  }
  citation +=
    citationInfo.publicationDate && citationInfo.publicationDate.length > 0
      ? ` (${moment.utc(citationInfo.publicationDate).format("YYYY")}), `
      : ` [${i18n.t("citations.insert-year-publication")}], `;
  citation += "<em>";
  citation += citationInfo.title ? citationInfo.title : "";
  let intEdition = parseInt(citationInfo.edition, 10);
  if (citationInfo.edition && intEdition > 1) {
    citation +=
      ", " +
      i18n.t("library.book.edition", {
        edition: numeral(intEdition).format("0o")
      });
  }
  citation += "</em>";
  citation += citationInfo.publisher ? ", " + citationInfo.publisher : "";
  citation += `, [${i18n.t("citations.insert-city-publication")}].`;
  citation += ` ${i18n.t("citations.available-from", {
    location: Fraction.Brand.brand
  })}.`;
  return citation;
};

export const getApaCitationForBook = citationInfo => {
  let citation = parseMultipleAuthors(citationInfo, CITATION.apa);
  if (!citation && citationInfo.author.length > 0) {
    let author = citationInfo.author[0];
    if (author.last && author.last.trim().length > 0) {
      citation += author.last;
    }
    if (author.first && author.first.trim().length > 0) {
      citation += ", " + getInitial(author.first);
      if (author.middle && author.middle.trim().length > 0) {
        citation += " " + getInitial(author.middle);
      }
    } else {
      if (!citation) {
        citation = "";
      }
    }
    for (let i = 1; i < citationInfo.author.length; i++) {
      author = citationInfo.author[i];
      citation += ", " + author.last;
      if (author.first.length > 0) {
        citation += ", " + getInitial(author.first);
        if (author.middle.length > 0) {
          citation += " " + getInitial(author.middle);
        }
      }
    }
    if (citation.length > 1 && citation[citation.length - 1] !== ".") {
      citation += ".";
    }
  }

  if (citationInfo.publicationDate && citationInfo.publicationDate.length > 0) {
    citation +=
      " (" + moment.utc(citationInfo.publicationDate).format("YYYY") + ").";
  }
  if (citationInfo.title) {
    citation += " <em>" + citationInfo.title;
  }
  if (
    citationInfo.edition &&
    citationInfo.edition.length > 0 &&
    parseInt(citationInfo.edition, 10) > 0
  ) {
    citation +=
      ", " +
      i18n.t("library.book.edition", {
        edition: numeral(parseInt(citationInfo.edition, 10)).format("0o")
      }) +
      "</em>.";
  } else {
    citation += "</em>.";
  }
  citation += ` [${Fraction.Brand.brand}].`;
  citation +=
    ` ${i18n.t("citations.retrieved-from")} <span class='retrieved-from'>` +
    document.location.protocol +
    "//" +
    document.location.host +
    "/#/books/" +
    citationInfo.isbn +
    "/ </span>";

  return citation;
};

export const getVancouverCitationForBook = citationInfo => {
  let citation = parseMultipleAuthors(citationInfo, CITATION.van);
  if (!citation && citationInfo.author.length > 0) {
    let author = citationInfo.author[0];
    if (author.last && author.last.trim().length > 0) {
      citation += author.last;
    }
    if (author.first && author.first.trim().length > 0) {
      citation += " " + getInitial(author.first);
    } else {
      if (!citation) {
        citation = "";
      }
    }
    for (let i = 1; i < citationInfo.author.length; i++) {
      author = citationInfo.author[i];
      citation += " " + author.last;
      if (author.first.length > 0) {
        citation += " " + getInitial(author.first);
      }
    }
  }

  //we currently do not have title of article so using placeholder.
  citation += ` [${i18n.t("citations.insert-title-article")}]. `;
  citation += "<em>";
  citation += citationInfo.title
    ? citationInfo.title
    : ` [${i18n.t("citations.insert-title-journal")}].`;
  citation += "</em>.";
  citation +=
    citationInfo.publicationDate && citationInfo.publicationDate.length > 0
      ? ` ${moment.utc(citationInfo.publicationDate).format("YYYY")};`
      : ` [${i18n.t("citations.insert-year-publication")}];`;
  //we currently do not Volume / Issue Numbers so outputting placeholder.
  citation += ` [${i18n.t("citations.insert-volume-number")}];`;
  //we currently do not pull Page Count from manage so outputting placeholder.
  citation += ` [${i18n.t("citations.insert-page-numbers-article")}].`;
  citation += ` ${i18n.t("citations.available-from", {
    location:
      document.location.protocol +
      "//" +
      document.location.host +
      "/#/books/" +
      citationInfo.isbn +
      "/"
  })}`;
  //we are outputting the journal format and date needed to be displayed at the bottom
  citation += ` [${moment().format("YYYY MMM DD")}].`;

  return citation;
};

export const getABNTCitationForBook = citationInfo => {
  let citation = parseMultipleAuthors(citationInfo, CITATION.abnt);
  if (!citation && citationInfo.author.length > 0) {
    let author = citationInfo.author[0];
    if (author.last && author.last.trim().length > 0) {
      citation += author.last;
    }
    if (author.first && author.first.trim().length > 0) {
      citation += ", " + getInitial(author.first);
      if (author.middle && author.middle.trim().length > 0) {
        citation += " " + getInitial(author.middle);
      }
    } else {
      if (!citation) {
        citation = "";
      }
    }
    for (let i = 1; i < citationInfo.author.length; i++) {
      author = citationInfo.author[i];
      citation += ", " + author.last;
      if (author.first.length > 0) {
        citation += ", " + getInitial(author.first);
        if (author.middle.length > 0) {
          citation += " " + getInitial(author.middle);
        }
      }
    }
    if (citation.length > 1 && citation[citation.length - 1] !== ".") {
      citation += ".";
    }
  }

  citation += " <strong>";
  citation += citationInfo.title
    ? citationInfo.title
    : `[${i18n.t("citations.insert-title-ebook")}].`;
  citation += "</strong>.";
  //we currently do not have Publisher Location so outputting placeholder.
  citation += ` [${i18n.t("citations.insert-publisher-location")}]:`;
  citation +=
    citationInfo.publisher && citationInfo.publisher.length > 0
      ? ` ${citationInfo.publisher},`
      : ` [${i18n.t("citations.insert-publisher-name")}],`;
  citation +=
    citationInfo.publicationDate && citationInfo.publicationDate.length > 0
      ? ` ${moment.utc(citationInfo.publicationDate).format("YYYY")}.`
      : ` [${i18n.t("citations.insert-year-publication")}].`;
  citation += ` ${citationInfo.isbn}.`;
  citation += ` ${i18n.t("citations.abnt-url", {
    url:
      document.location.protocol +
      "//" +
      document.location.host +
      "/#/books/" +
      citationInfo.isbn +
      "/"
  })}.`;
  citation += ` ${i18n.t("citations.abnt-access-date", {
    date: moment().format("DD MMM YYYY")
  })}`;

  return citation;
};
